/**
 * Builds a `StoredDocument` from a picked file plus the metadata the user
 * supplied. One implementation, so every upload surface produces records of the
 * same shape — a document uploaded from an agent's step 1 is indistinguishable
 * from one uploaded on the bench, which is what makes it re-usable.
 */

import { uid } from '@/lib/random';
import type { AccessLevel, DocumentAgentType, StoredDocument } from '../contracts';

export interface DocumentDraft {
  readonly file: File;
  readonly name: string;
  readonly source: string;
  readonly category: string;
  readonly agentType: DocumentAgentType;
  readonly accessLevel: AccessLevel;
  readonly dataQualityScore: number;
  readonly brand?: string;
  readonly partner?: string;
  readonly description?: string;
  readonly accessUsers?: readonly string[];
}

export function buildStoredDocument(draft: DocumentDraft, now: Date = new Date()): StoredDocument {
  const { file } = draft;
  return {
    id: uid('doc'),
    name: draft.name,
    fileName: file.name,
    size: file.size,
    // Browsers leave `type` empty for some Office formats; record that honestly
    // rather than guessing a MIME type from the extension.
    mimeType: file.type === '' ? 'application/octet-stream' : file.type,
    uploadedAt: now.toISOString(),
    source: draft.source,
    category: draft.category,
    agentType: draft.agentType,
    accessLevel: draft.accessLevel,
    dataQualityScore: draft.dataQualityScore,
    ...(draft.brand === undefined || draft.brand === '' ? {} : { brand: draft.brand }),
    ...(draft.partner === undefined || draft.partner === '' ? {} : { partner: draft.partner }),
    ...(draft.description === undefined || draft.description === ''
      ? {}
      : { description: draft.description }),
    ...(draft.accessLevel === 'restricted' && draft.accessUsers !== undefined
      ? { accessUsers: [...draft.accessUsers] }
      : {}),
    // The File itself is a Blob, and structured-clone stores it directly.
    blob: file,
  };
}
